import { Autocomplete, Box, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchProductLines } from '@sophocles/features/warehouse/productCategoryApi';

const ProductCategorySelect = ({ values, onChange }) => {
  const { t } = useTranslation();
  const [productLines, setProductLines] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedSubFamily, setSelectedSubFamily] = useState(null);

  useEffect(() => {
    fetchProductLines()
      .then(data => {
        setProductLines(data.results);
      })
      .catch(error => console.error('Error fetching product lines:', error));
  }, []);

  useEffect(() => {
    // Find and set selected items based on passed values
    if (values?.product_line) {
      const line = productLines.find(line => line.id === values.product_line);
      setSelectedLine(line);

      if (line && values?.product_family) {
        const family = line.families.find(fam => fam.id === values.product_family);
        setSelectedFamily(family);

        if (family && values?.product_sub_family) {
          const subFamily = family.sub_families.find(sub => sub.id === values.product_sub_family);
          setSelectedSubFamily(subFamily);
        }
      }
    }
  }, [values]); // Re-run when values change

  const handleLineChange = (event, newValue) => {
    setSelectedLine(newValue);
    setSelectedFamily(null); // Reset family when the line changes
    setSelectedSubFamily(null); // Reset subfamily when the line changes
    onChange(newValue, null, null);
  };

  const handleFamilyChange = (event, newValue) => {
    setSelectedFamily(newValue);
    setSelectedSubFamily(null); // Reset subfamily when the family changes
    onChange(selectedLine, newValue, null);
  };

  const handleSubFamilyChange = (event, newValue) => {
    setSelectedSubFamily(newValue);
    onChange(selectedLine, selectedFamily, newValue);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', gap: 2 }}>
      {/* Product Line Selection */}
      <Autocomplete
        fullWidth
        size="small"
        options={productLines}
        getOptionLabel={option => option.name}
        getOptionSelected={(option, value) => option.id === value}
        value={selectedLine}
        onChange={handleLineChange}
        renderInput={params => (
          <TextField {...params} label={t('warehouse.product.categoryLine', 'Product Line')} variant="outlined" />
        )}
      />

      {/* Product Family Selection (depends on selected line) */}
      <Autocomplete
        fullWidth
        size="small"
        options={selectedLine ? selectedLine.families : []}
        getOptionLabel={option => option.name}
        getOptionSelected={(option, value) => option.id === value}
        value={selectedFamily}
        onChange={handleFamilyChange}
        disabled={!selectedLine} // Disable until a line is selected
        renderInput={params => (
          <TextField {...params} label={t('warehouse.product.categoryFamily', 'Product Family')} variant="outlined" />
        )}
      />

      {/* Product Sub-Family Selection (depends on selected family) */}
      <Autocomplete
        fullWidth
        size="small"
        options={selectedFamily ? selectedFamily.sub_families : []}
        getOptionLabel={option => option.name}
        getOptionSelected={(option, value) => option.id === value}
        value={selectedSubFamily}
        onChange={handleSubFamilyChange}
        disabled={!selectedFamily} // Disable until a family is selected
        renderInput={params => (
          <TextField
            {...params}
            label={t('warehouse.product.categorySubFamily', 'Product Sub-Family')}
            variant="outlined"
          />
        )}
      />
    </Box>
  );
};

export default ProductCategorySelect;
